import React, { useEffect, useRef, useCallback } from 'react';
import { useServiceTracking } from 'components/hooks/tracking/useServiceTracking';
import { FeatureTracked, TopicTracked } from 'models/tracking';
import { useApplicationContext } from 'components/hooks/UseApplicationContext';
import { useMatch } from 'react-router';
import { RoutePaths } from 'routes/RoutePaths';

declare global {
    namespace React {
        namespace JSX {
            interface IntrinsicElements {
                'sgwt-help-center': any;
            }
        }
    }
}

type HelpCenterRefElementFunc = (mountedElement: HTMLElement | null) => void;
export const HelpCenter: React.FC = () => {
    const { trackAction } = useServiceTracking();
    const { hasOnlyB2b2cAccess } = useApplicationContext();

    const setHelpCenterEventListenersRef = useRef<HelpCenterRefElementFunc>(null);
    const previousEltMountedRef = useRef<HTMLElement>(null);

    const onOpenTracking = useCallback(async () => {
        await trackAction(FeatureTracked.OpenHelpCenter, {
            topic: TopicTracked.Help,
        });
    }, [trackAction]);

    const isCountryRestricted = useMatch(RoutePaths.Disclaimer.CountryRestricted.route);
    let messageOnly = false;
    if (isCountryRestricted) {
        messageOnly = true;
    }

    useEffect(() => {
        setHelpCenterEventListenersRef.current = (
            mountedElement: HTMLElement | null,
        ) => {
            if (mountedElement) {
                // eslint-disable-next-line @eslint-react/web-api-no-leaked-event-listener
                mountedElement.addEventListener(
                    'sgwt-help-center--open',
                    onOpenTracking,
                );
                // Some times mountedElement is different between re rendering, we keep last HTMLElement to remove removeEventListener
                previousEltMountedRef.current = mountedElement;
            }
        };

        return () => {
            // Remove previous event handler
            if (previousEltMountedRef.current) {
                previousEltMountedRef.current.removeEventListener(
                    'sgwt-help-center--open',
                    onOpenTracking,
                );
            }
        };
    }, [onOpenTracking]);

    const application = hasOnlyB2b2cAccess
        ? 'service-board-b2b2c'
        : 'service-board';

    return <aside>
        <sgwt-help-center
            ref={setHelpCenterEventListenersRef.current}
            id="sgwtSuperHelpCenter"
            sg-connect-support="sg-connect-v2"
            application-id={application}
            introduction-tour={!hasOnlyB2b2cAccess}
            message-only={messageOnly}
        />
    </aside>;
};

export const openHelpCenterForm = (): void => {
    const helpCenter = getHelpCenter();
    if (helpCenter) {
        helpCenter.form();
    }
};

export const openHelpCenter = (): void => {
    const helpCenter = getHelpCenter();
    if (helpCenter) {
        helpCenter.open();
    }
};

export const openHelpCenterTopic = (topic: string): void => {
    const helpCenter = getHelpCenter();
    if (helpCenter) {
        helpCenter.topic(topic);
    }
};

const getHelpCenter = (): any => {
    return document.querySelector('sgwt-help-center') as any;
};